"use client";

import {
  FacebookIcon,
  RedditIcon,
  TelegramIcon,
  WhatsAppIcon,
  XIcon,
} from "../common/icons";
import {
  FacebookShareButton,
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import { LinkIcon, MailIcon } from "lucide-react";
import { ShareBarEvents, trackEvent } from "@punchup/analytics";

import Link from "next/link";
import type { ShareDataWithUrl } from "../common/ShareModal";
import { cn } from "@punchup/utils/cn";
import { toast } from "../../shadcn/ui/use-toast";

const shareButtonStyle = {
  padding: "8px 32px 8px 12px",
  borderRadius: "6px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "rgb(229 229 229)",
  "&:hover": {
    backgroundColor: "green",
  },
};

const shareButtonClassNames =
  "flex flex-row items-center justify-start gap-6 transition-colors duration-200 ease-in-out";

interface Props {
  shareData: ShareDataWithUrl;
  className?: string;
  onLinkCopied?: () => void;
}

export default function ShareBar({
  shareData: { title, text, url },
  className,
  onLinkCopied,
}: Props) {
  const copyToClipboard = (url: string) => navigator.clipboard.writeText(url);

  const onCopyLinkClick = () => {
    trackEvent(ShareBarEvents.COPIED_LINK_TO_CLIPBOARD);

    copyToClipboard(url).then(() => {
      toast({
        title: "Link Copied to Clipboard!",
        variant: "success",
      });
      onLinkCopied && onLinkCopied();
    });
  };

  const generateMailToLink = () => {
    let subject = "";
    if (text) {
      subject = encodeURIComponent(text);
    }
    const body = encodeURIComponent(`${url}`);

    return `mailto:?subject=${subject}&body=${body}`;
  };

  const mailToLink = generateMailToLink();

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <FacebookShareButton
        onClick={() => {
          trackEvent(ShareBarEvents.SHARED_TO_MEDIA, {
            shareType: "facebook",
          });
        }}
        className={shareButtonClassNames}
        style={shareButtonStyle}
        title={title}
        url={url}
      >
        <FacebookIcon width={24} height={24} />
        <LinkLabel>Share on Facebook</LinkLabel>
      </FacebookShareButton>
      <TwitterShareButton
        onClick={() => {
          trackEvent(ShareBarEvents.SHARED_TO_MEDIA, {
            shareType: "twitter",
          });
        }}
        className={shareButtonClassNames}
        style={shareButtonStyle}
        title={title}
        url={url}
      >
        <div className="flex flex-col items-center justify-center w-6 h-6 p-1 bg-black rounded-sm">
          <XIcon width={18} height={18} className="text-white" />
        </div>
        <LinkLabel>Post on X</LinkLabel>
      </TwitterShareButton>
      <RedditShareButton
        onClick={() => {
          trackEvent(ShareBarEvents.SHARED_TO_MEDIA, {
            shareType: "reddit",
          });
        }}
        className={shareButtonClassNames}
        style={shareButtonStyle}
        title={title}
        url={url}
      >
        <div className="overflow-hidden rounded-sm">
          <RedditIcon width={24} height={24} />
        </div>
        <LinkLabel>Post on Reddit</LinkLabel>
      </RedditShareButton>
      <WhatsappShareButton
        onClick={() => {
          trackEvent(ShareBarEvents.SHARED_TO_MEDIA, {
            shareType: "whatsapp",
          });
        }}
        className={shareButtonClassNames}
        style={shareButtonStyle}
        title={title}
        url={url}
      >
        <div className="overflow-hidden rounded-sm">
          <WhatsAppIcon width={24} height={24} />
        </div>
        <LinkLabel>Send on WhatsApp</LinkLabel>
      </WhatsappShareButton>
      <TelegramShareButton
        onClick={() => {
          trackEvent(ShareBarEvents.SHARED_TO_MEDIA, {
            shareType: "telegram",
          });
        }}
        className={shareButtonClassNames}
        style={shareButtonStyle}
        title={title}
        url={url}
      >
        <TelegramIcon width={24} height={24} />
        <LinkLabel>Send on Telegram</LinkLabel>
      </TelegramShareButton>
      <Link
        onClick={() => {
          trackEvent(ShareBarEvents.SHARED_TO_MEDIA, {
            shareType: "email",
          });
        }}
        className={shareButtonClassNames}
        style={shareButtonStyle}
        href={mailToLink}
      >
        <MailIcon className="w-[28px] h-[28px] p-1 bg-slate-100 border border-slate-200 rounded-md" />
        <LinkLabel>Send via Email</LinkLabel>
      </Link>
      <button
        title="Copy Link to Share Content"
        onClick={onCopyLinkClick}
        className={shareButtonClassNames}
        style={shareButtonStyle}
      >
        <LinkIcon className="w-[28px] h-[28px] p-1 bg-slate-100 border border-slate-200 rounded-md" />
        <LinkLabel>Copy link</LinkLabel>
      </button>
    </div>
  );
}

const LinkLabel = ({ children }: { children: string }) => (
  <p className="font-medium text-neutral-800">{children}</p>
);
